import type { WidgetRelatedApp } from '@inno-tools/container-bridge'
import { WIDGETS_CONFIG } from 'modules/desktop/modules/widgets/constants/widgetsConfig'
import type {
  ArtifactCategory,
  ArtifactSummary
} from 'shared/api/forgeApi/forgeApi.types'
import { WidgetKey } from 'shared/constants/widgets'
import type { User } from 'shared/types/user'
import type { WidgetSpec } from 'shared/types/widgets'

// UI-only sentinel. Never send it as a Forge category_id.
export const OTHER_CATEGORY_ID = 'uncategorized'
export const BUILT_IN_WIDGET_CATEGORIES: Partial<Record<WidgetKey, string>> = {
  [WidgetKey.TASK_MANAGEMENT]: 'tasks-projects',
  [WidgetKey.TODOS]: 'tasks-projects',
  [WidgetKey.CALENDAR]: 'time-scheduling',
  [WidgetKey.CLOCK]: 'time-scheduling',
  [WidgetKey.TIME_TRACKING]: 'time-scheduling',
  [WidgetKey.GOAL_WIDGET]: 'sales-customers',
  [WidgetKey.CSM_PERFORMANCE_OVERVIEW]: 'sales-customers',
  [WidgetKey.BUDGET]: 'people-teams',
  [WidgetKey.TIPS]: 'knowledge-learning'
}

export interface MarketplaceCategoryGroup {
  id: string
  name: string
  widgets: WidgetSpec[]
  artifacts: ArtifactSummary[]
}

export const categoryForRelatedApp = (
  app?: WidgetRelatedApp | null,
  user: User | null = null
): string | null => {
  const widget = Object.values(WIDGETS_CONFIG).find(
    widget =>
      widget.relatedApp === app &&
      !widget.isHidden?.(user) &&
      !widget.marketplaceHidden
  )
  return widget ? (BUILT_IN_WIDGET_CATEGORIES[widget.key] ?? null) : null
}

export const buildMarketplaceCategoryGroups = (
  categories: ArtifactCategory[],
  widgets: WidgetSpec[],
  artifacts: ArtifactSummary[],
  otherLabel: string
): MarketplaceCategoryGroup[] => {
  const groups: MarketplaceCategoryGroup[] = categories.map(category => ({
    id: category.id,
    name: category.name,
    widgets: [],
    artifacts: []
  }))
  const other: MarketplaceCategoryGroup = {
    id: OTHER_CATEGORY_ID,
    name: otherLabel,
    widgets: [],
    artifacts: []
  }
  const byId = new Map(groups.map(group => [group.id, group]))
  for (const widget of widgets) {
    const group =
      byId.get(BUILT_IN_WIDGET_CATEGORIES[widget.widgetKey] ?? '') ?? other
    group.widgets.push(widget)
  }
  for (const artifact of artifacts) {
    if (artifact.kind && artifact.kind !== 'widget') continue
    const group = byId.get(artifact.category_id ?? '') ?? other
    group.artifacts.push(artifact)
  }
  return [...groups, other].filter(
    group => group.widgets.length || group.artifacts.length
  )
}
